import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, ArrowLeft, ArrowRight } from 'lucide-react';
import { BACKEND_URL } from '../utils/api';
import INDUSTRIES from '../data/industries';
import SEOHead, { createArticleSchema, createBreadcrumbSchema, createOrganizationSchema, createFAQSchema } from './SEOHead';

const COLORS = { primary: '#002FA7', accent: '#FFCC00', dark: '#0A1628', light: '#F8FAFC' };

export default function BlogArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/blog/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(data => {
        setPost(data);
        // Fetch related posts
        if (data.industry) {
          fetch(`${BACKEND_URL}/api/blog/posts?industry=${data.industry}&limit=3`)
            .then(r => r.json())
            .then(d => setRelatedPosts((d.posts || []).filter(p => p.slug !== slug).slice(0, 2)))
            .catch(() => {});
        }
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLORS.light }}>
        <div className="animate-pulse text-slate-400">Loading article...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLORS.light }}>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>Article Not Found</h1>
          <Link to="/blog" className="text-blue-600 hover:underline flex items-center gap-2 justify-center">
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const industryName = INDUSTRIES.find(i => i.slug === post.industry)?.name || post.industry || '';
  const publishDate = post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  const schema = [
    createOrganizationSchema(),
    createArticleSchema({ title: post.title, excerpt: post.excerpt, image: post.image, date: post.published_at, slug: post.slug }),
    createBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Industry News', url: '/blog' },
      { name: post.title }
    ])
  ];

  return (
    <>
      <SEOHead
        title={`${post.title} | LocalBranding Pro Blog`}
        description={post.excerpt}
        ogImage={post.image}
        ogType="article"
        schema={schema}
      />

      <div className="min-h-screen" style={{ background: COLORS.light }}>
        {/* Nav */}
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="article-nav-logo">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: COLORS.primary }}>
                <span className="text-white font-bold text-sm">LB</span>
              </div>
              <span className="font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>LocalBranding Pro</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/#features" className="text-sm text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/blog" className="text-sm font-medium" style={{ color: COLORS.primary }}>Industry News</Link>
              <Link to="/admin/login" className="px-4 py-2 rounded-full text-sm font-semibold text-white" style={{ background: COLORS.primary }}>
                Start Free Trial
              </Link>
            </div>
          </div>
        </nav>

        {/* Article */}
        <article className="max-w-3xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex text-xs text-slate-500 mb-8" aria-label="Breadcrumb" data-testid="article-breadcrumb">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-slate-700">Industry News</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-400 truncate max-w-[200px]">{post.title}</span>
          </nav>

          {/* Meta */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.primary }}>{industryName.replace(/-/g, ' ')}</span>
            <span className="text-xs text-slate-400">{publishDate}</span>
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <Clock className="h-3 w-3" /> {post.read_time}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }} data-testid="article-title">
            {post.title}
          </h1>

          {/* Featured Image */}
          {post.image && (
            <figure className="mb-8">
              <img src={post.image} alt={post.image_alt || post.title} className="w-full rounded-xl shadow-lg object-cover aspect-[2/1]" />
              {post.image_alt && <figcaption className="text-xs text-slate-400 mt-2 text-center">{post.image_alt}</figcaption>}
            </figure>
          )}

          {/* Content */}
          <div
            className="prose prose-slate prose-lg max-w-none mb-12"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            dangerouslySetInnerHTML={{ __html: post.content }}
            data-testid="article-content"
          />

          {/* Keywords */}
          {post.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.keywords.map((kw, i) => (
                <span key={i} className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600">{kw}</span>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm text-center mb-12">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              Grow your {industryName.toLowerCase().replace(/-/g, ' ')} business with LocalBranding Pro
            </h3>
            <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Manage listings, reviews, SEO, and social media from one dashboard.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/admin/login" className="px-6 py-2.5 rounded-full text-sm font-semibold text-white" style={{ background: COLORS.primary }} data-testid="article-cta">
                Start Free Trial
              </Link>
              {post.industry && (
                <Link to={`/industries/${post.industry}`} className="px-6 py-2.5 rounded-full text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
                  {industryName.replace(/-/g, ' ')} Features
                </Link>
              )}
            </div>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div>
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>More from the Blog</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {relatedPosts.map(rp => (
                  <Link key={rp.slug} to={`/blog/${rp.slug}`} className="group flex gap-4 bg-white rounded-lg p-3 border border-slate-100 hover:shadow-md transition-shadow" data-testid={`related-${rp.slug}`}>
                    <img src={rp.image} alt={rp.image_alt || rp.title} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                    <div>
                      <span className="text-xs font-semibold uppercase" style={{ color: COLORS.primary }}>
                        {(INDUSTRIES.find(i => i.slug === rp.industry)?.name || rp.industry || '').replace(/-/g, ' ')}
                      </span>
                      <h4 className="text-sm font-bold group-hover:text-blue-700 transition-colors line-clamp-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {rp.title}
                      </h4>
                      <span className="text-xs text-slate-400">{rp.read_time}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Footer */}
        <footer className="py-8 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} LocalBranding Pro. All rights reserved.</p>
            <p className="text-xs text-slate-500">A <a href="https://web-techservices.com" className="text-slate-600 hover:text-slate-800 transition-colors">Web-TechServices AI</a> product.</p>
            <div className="flex gap-4 text-xs text-slate-500">
              <Link to="/" className="hover:text-slate-700">Home</Link>
              <Link to="/blog" className="hover:text-slate-700">Industry News</Link>
              <Link to="/admin/login" className="hover:text-slate-700">Admin Login</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
